import React from "react";
import type { ColumnsType } from "antd/es/table";
import { Localize } from "components/service";
import CommunicationFields from "../fields";
import { formatCommunicationDateTime } from "./communication-date-helper";
import type {
  TCommunicationAuditEntry,
  TCommunicationReferenceOption,
  TCommunicationTab,
  TCommunicationTask,
  TCommunicationTaskFormValues,
} from "./communication-types-helper";

interface ICreateCommunicationColumnsProps {
  currentTab: TCommunicationTab;
  canEditRaw: boolean;
  canApprove: boolean;
  equipmentOptions: TCommunicationReferenceOption[];
  flooringOptions: TCommunicationReferenceOption[];
  organizerOptions: TCommunicationReferenceOption[];
  exhibitionOptions: TCommunicationReferenceOption[];
  auditEntryMap: Record<string, TCommunicationAuditEntry | undefined>;
  onSave: (taskId: string, values: TCommunicationTaskFormValues) => void;
  onApprove: (taskId: string, values: TCommunicationTaskFormValues) => void;
}

const baseColumns = (): ColumnsType<TCommunicationTask> => [
  {
    title: <Localize>COMMUNICATION.Table_Email</Localize>,
    dataIndex: "sender",
    key: "sender",
    width: "18%",
    sorter: true,
    render: (_, task) => <CommunicationFields.Info task={task} />,
  },
  {
    title: <Localize>COMMUNICATION.Table_EventCompany</Localize>,
    dataIndex: "event_name",
    key: "event_name",
    width: "16%",
    sorter: true,
    render: (_, task) => <CommunicationFields.Overview task={task} />,
  },
  {
    title: <Localize>COMMUNICATION.Table_Stand</Localize>,
    dataIndex: "stand_number",
    key: "stand_number",
    width: "10%",
    sorter: true,
    render: (value: string) => (
      <span className="communication-table__stand-value">{value || "\u2014"}</span>
    ),
  },
  {
    title: <Localize>COMMUNICATION.Table_Changes</Localize>,
    dataIndex: "final_changes",
    key: "final_changes",
    render: (_, task) => <CommunicationFields.Changes task={task} />,
  },
  {
    title: <Localize>COMMUNICATION.Table_Questions</Localize>,
    dataIndex: "questions",
    key: "questions",
    width: "12%",
    sorter: true,
    render: (_, task) => <CommunicationFields.Questions task={task} />,
  },
];

const operationalColumns = (
  props: ICreateCommunicationColumnsProps
): ColumnsType<TCommunicationTask> => [
  ...baseColumns(),
  {
    title: <Localize>COMMUNICATION.Table_NextStep</Localize>,
    dataIndex: "status",
    key: "status",
    width: "12%",
    sorter: true,
    render: (_, task) => <CommunicationFields.NextStep task={task} />,
  },
  {
    title: <Localize>COMMUNICATION.Table_Received</Localize>,
    dataIndex: "received_at",
    key: "received_at",
    width: "12%",
    sorter: true,
    render: (value: string) => (
      <span className="communication-table__date-value">
        {formatCommunicationDateTime(value) || "\u2014"}
      </span>
    ),
  },
  {
    title: <Localize>COMMUNICATION.Table_Action</Localize>,
    dataIndex: "action",
    key: "action",
    width: "12%",
    align: "right",
    render: (_, task) => (
      <CommunicationFields.Action
        task={task}
        canEditRaw={props.canEditRaw}
        canApprove={props.canApprove}
        currentTab={props.currentTab}
        equipmentOptions={props.equipmentOptions}
        flooringOptions={props.flooringOptions}
        organizerOptions={props.organizerOptions}
        exhibitionOptions={props.exhibitionOptions}
        auditEntry={props.auditEntryMap[task.id]}
        onSave={props.onSave}
        onApprove={props.onApprove}
      />
    ),
  },
];

const auditColumns = (
  props: ICreateCommunicationColumnsProps
): ColumnsType<TCommunicationTask> => [
  ...baseColumns(),
  {
    title: <Localize>TABLES.Column_Title_ApprovedBy</Localize>,
    dataIndex: "approved_by",
    key: "approved_by",
    width: "12%",
    sorter: true,
    render: (value: string) => (
      <span className="communication-table__user-value">{value || "\u2014"}</span>
    ),
  },
  {
    title: <Localize>TABLES.Column_Title_ApprovedAt</Localize>,
    dataIndex: "approved_at",
    key: "approved_at",
    width: "14%",
    sorter: true,
    render: (value: string) => (
      <span className="communication-table__date-value">
        {value ? formatCommunicationDateTime(value) : "\u2014"}
      </span>
    ),
  },
  {
    title: <Localize>COMMUNICATION.Table_Action</Localize>,
    dataIndex: "action",
    key: "action",
    width: "10%",
    align: "right",
    render: (_, task) => (
      <CommunicationFields.Action
        task={task}
        canEditRaw={false}
        canApprove={false}
        currentTab={props.currentTab}
        equipmentOptions={props.equipmentOptions}
        flooringOptions={props.flooringOptions}
        organizerOptions={props.organizerOptions}
        exhibitionOptions={props.exhibitionOptions}
        auditEntry={props.auditEntryMap[task.id]}
        onSave={props.onSave}
        onApprove={props.onApprove}
      />
    ),
  },
];

export const createCommunicationColumns = (
  props: ICreateCommunicationColumnsProps
): ColumnsType<TCommunicationTask> =>
  props.currentTab === "audit" ? auditColumns(props) : operationalColumns(props);
